import { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomObjectWizard from 'components/Wizards/CustomObject';
import FieldService from 'services/FieldService';
import CustomObjectsService from 'services/CustomObjectsService';
import {
  getToastConfig,
  isContact,
  saveStep,
} from 'components/Wizards/CustomObject/utilities';
import { useToast } from 'components/ToastProvider';
import { PermissionsError } from 'services/errors';
import { serviceRefreshAccessStart } from 'store/authentication/authenticationAction';
import { useDispatch } from 'react-redux';
import Loader from 'components/Kizen/Loader';
import { PageContent } from './styles';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { getBackButtonTitle } from './helpers';
import { useQuery } from '@tanstack/react-query';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import { useQueryClient } from '@tanstack/react-query';

const CustomObjectSettingsPage = () => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const history = useHistory();
  const { objectId } = useParams();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [isAddingContext, setIsAddingContext] = useState(objectId === 'new');

  const [navigationState] = useState(() => {
    const params = new URLSearchParams(history.location.search);
    return {
      from: params.get('from') || history.location?.state?.from || null,
      fromKey:
        params.get('fromKey') || history.location?.state?.fromKey || null,
      focusStepKey:
        params.get('focusStepKey') ||
        history.location?.state?.focusStepKey ||
        null,
      customLayoutTabKey:
        params.get('customLayoutTabKey') ||
        history.location?.state?.customLayoutTabKey ||
        null,
      fieldId:
        params.get('fieldId') || history.location?.state?.fieldId || null,
    };
  });

  const {
    data: model,
    isLoading: loading,
    refetch: refetchModel,
  } = useQuery({
    queryKey: CUSTOM_OBJECTS.MODEL(objectId),
    queryFn: async () => {
      if (!objectId || objectId === 'new') {
        return null;
      }
      const model = await FieldService.getModel({ id: objectId });
      return model;
    },
  });

  useSetTitleOnLoad(
    objectId === 'new'
      ? t('New Custom Object')
      : t('{{objectName}} Settings', model)
  );

  const onSaveButtonClick = useCallback(
    async (payload, model, activeStep) => {
      const { id } = await saveStep(payload, model);

      if (id && activeStep.key === 'stageSettings') {
        // the board view uses react-query in the quick add modal, so we need to invalidate the query
        queryClient.invalidateQueries({
          queryKey: CUSTOM_OBJECTS.DETAILS(id),
        });
      }

      if (!model) {
        history.replace({
          pathname: `/custom-objects/${id}/settings`,
          search: history.location.search,
          state: history.location.state,
        });
        setIsAddingContext(true);
      }
      if (model === null) {
        dispatch(serviceRefreshAccessStart());
      }
      showToast(getToastConfig(t, !model, isContact(model)).success);
    },
    [showToast, t, queryClient, history, dispatch]
  );

  const handleDeleteCustomObject = useCallback(async () => {
    try {
      await CustomObjectsService.deleteCustomObject({ id: objectId });
      showToast(getToastConfig(t).successDeleted);
    } catch (error) {
      showToast(getToastConfig(t).errorDeleted);
    }
  }, [objectId, showToast, t]);

  const handleRedirectOnDeletion = useCallback(() => {
    history.replace(`/custom-objects`);
  }, [history]);

  useEffect(() => {
    const prereleaseFeatureOverride = new URLSearchParams(
      history.location.search
    ).get('prerelease_feature_override');
    history.replace(
      prereleaseFeatureOverride
        ? `${history.location.pathname}?prerelease_feature_override=${prereleaseFeatureOverride}`
        : history.location.pathname,
      navigationState
    );
  }, [history, navigationState]);

  const backButtonTitle = getBackButtonTitle(
    t,
    history.location.state?.fromKey
  );

  const goBackPath =
    history.location.state?.from ||
    (objectId === 'new' ? '/custom-objects' : `/custom-objects/${objectId}`);

  const handleHide = useCallback(
    () => history.push(goBackPath),
    [goBackPath, history]
  );

  if (model && !model.access.edit) {
    throw new PermissionsError();
  }

  const objectIdRef = useRef(objectId);

  return (
    <PageContent>
      <Loader loading={!model && objectId !== 'new'}>
        <CustomObjectWizard
          backButtonTitle={backButtonTitle}
          show
          isFetching={loading}
          isCreateMode={objectId === 'new'}
          goBackPath={goBackPath}
          onHide={handleHide}
          model={model}
          onConfirm={onSaveButtonClick}
          refetchCustomObject={refetchModel}
          isAddingContext={isAddingContext}
          objectIdRef={objectIdRef}
          setIsAddingContext={setIsAddingContext}
          handleDeleteCustomObject={handleDeleteCustomObject}
          redirectOnDeleteHandler={handleRedirectOnDeletion}
        />
      </Loader>
    </PageContent>
  );
};

export default CustomObjectSettingsPage;
